import { StrictMode, lazy } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { NormalizePath } from "@/components/normalize-path";
import { initClientSentry } from "@/lib/sentry";

initClientSentry();

import { QueryClient, QueryClientProvider, MutationCache } from "@tanstack/react-query";
// Side-effect import: points TanStack Query's onlineManager at our
// reachability-verified connectivity instead of the browser's lying flag.
// MUST come before the QueryClient below. See query-connectivity.ts.
import "@/lib/query-connectivity";
import { toast } from "sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import { setToken, ApiError, rememberReturnPath } from "@/lib/api";

// Portal-entry "Open in Safari" handoff: the installed PWA can't share its
// localStorage session with real Safari, so the short-lived link minted by
// POST /api/backflow/job/:jobId/portal-entry-link carries a 30-minute token
// as ?portalToken=…. Promote it into the stored session BEFORE AuthProvider
// initializes (RequireAuth would otherwise bounce to /login), then strip it
// from the URL so it can't leak via history or shared links.
try {
    const bootParams = new URLSearchParams(window.location.search);
    const portalToken = bootParams.get("portalToken");
    if (portalToken) {
        setToken(portalToken);
        bootParams.delete("portalToken");
        const qs = bootParams.toString();
        window.history.replaceState(null, "", `${window.location.pathname}${qs ? `?${qs}` : ""}`);
    }
} catch {
    // URL parsing is best-effort; a malformed query string must never block boot.
}
import { AppShell } from "@/components/layout/app-shell";
import { ClockInGate } from "@/components/clock-in-gate";
import { PageSpinner } from "@/components/ui/spinner";
import { AuthUnreachable } from "@/components/auth-unreachable";
import { OfflineIndicator } from "@/components/offline-indicator";
import { InstallPrompt } from "@/components/install-prompt";
import { UpdateBanner } from "@/components/update-banner";
import { FailedSavesChip } from "@/components/failed-saves-chip";
import { registerServiceWorker } from "@/lib/sw-register";
import { registerPushSubscription } from "@/lib/push-register";

// Eager imports — landing pages, the hot office paths, and field core.
// AppShell wraps every authenticated route in a <Suspense fallback={<PageSpinner/>}>
// so the lazy() imports below resolve transparently on first nav.
import LoginPage from "@/pages/login";
import GoogleReturnPage from "@/pages/google-return";
import ForgotPasswordPage from "@/pages/forgot-password";
import SetPasswordPage from "@/pages/set-password";
import DashboardPage from "@/pages/dashboard";
import CommandPage from "@/pages/command/index";
import JobsPage from "@/pages/jobs/index";
import JobDetailPage from "@/pages/jobs/job-page-router";
import JobFormPage from "@/pages/jobs/form";
import SchedulePage from "@/pages/jobs/schedule";
import CustomersPage from "@/pages/customers/index";
import CustomerDetailPage from "@/pages/customers/detail";
import CustomerFormPage from "@/pages/customers/form";
import CustomerIntakePage from "@/pages/customers/intake";
import CommercialPage from "@/pages/commercial/index";
import CommercialDetailPage from "@/pages/commercial/detail";
import DispatchPage from "@/pages/dispatch/index";
import FieldPage from "@/pages/field/index";
import FieldJobDetailPage from "@/pages/field/job-detail";
import FieldChatPage from "@/pages/field/chat";
import FieldPoints from "@/pages/field/points";
import FieldProfilePage from "@/pages/field/profile";
import FieldTimeOffPage from "@/pages/field/time-off";
import HelpPage from "@/pages/help/index";
import FilingWalkthroughPage from "@/pages/help/filing-walkthrough";
const SettingsConsolePage = lazy(() => import("@/pages/settings/console"));
import ProfilePage from "@/pages/profile";
import SiteDetailPage from "@/pages/sites/detail";
import AssetDetailPage from "@/pages/assets/detail";
import CompliancePage from "@/pages/compliance/index";
import TestDueRosterPage from "@/pages/scheduling/test-due-roster";
import OfficeReviewPage from "@/pages/office-review/index";
import CloseOutPage from "@/pages/close-out/index";
const ReportInputQueuePage = lazy(() => import("@/pages/close-out/report-input-queue"));
import CommandCenterNextPage from "@/pages/command-center-next/index";
import NotFoundPage from "@/pages/not-found";
import PhotoImportPage from "@/pages/photo-import/index";
import { PortalShell } from "@/components/layout/portal-shell";
import { PortalAuthProvider } from "@/lib/portal-auth";
import { Toaster } from "@/components/ui/toaster";
import { AppDialogHost } from "@/components/ui/app-dialogs";
import { ErrorBoundary } from "@/components/error-boundary";
import "./index.css";

// Lazy imports — heavy or niche pages. Pulling these out shrinks the
// entry bundle by ~250 kB gzipped and keeps the leaflet (149 kB),
// zxing (411 kB), pdfjs (518 kB), recharts (318 kB) chunks off the
// boot path for the routes that never visit them. Grouped by trigger:
//
// Dispatch/maps — leaflet
const DispatchMapPage = lazy(() => import("@/pages/dispatch/map"));
const DispatchRoutePlannerPage = lazy(() => import("@/pages/dispatch/route-planner"));
const OpsCenterPage = lazy(() => import("@/pages/ops/index"));
// Field workflow — leaflet + heavy state machine (~4,900 lines each)
const BackflowWorkflowPage = lazy(() => import("@/pages/field/backflow-workflow"));
const FireSafetyWorkflowPage = lazy(() => import("@/pages/field/fire-safety-workflow"));
const WorkOrderWorkflowPage = lazy(() => import("@/pages/field/work-order-workflow"));
const WorkOrderPdfEditorPage = lazy(() => import("@/pages/field/work-order-pdf-editor"));
// Charts — recharts (dashboard.tsx is eager so the charts chunk loads
// for office users on boot regardless; lazy-loading the secondary
// chart pages still shrinks the entry bundle by their own module weight)
const AnalyticsPage = lazy(() => import("@/pages/analytics/index"));
const ProfitabilityPage = lazy(() => import("@/pages/profitability/index"));
const ProfitabilityDetailPage = lazy(() => import("@/pages/profitability/detail"));
// The Money hub (/money?tab=…) bundles the old accounting worklist pages
// (ar-aging, invoices/estimates-attention, failed-payments, credit-memos,
// qbo-sync-issues) as tab sections — their old routes redirect below.
const MoneyPage = lazy(() => import("@/pages/money"));
// #1916 — one check, many jobs.
const RecordPaymentPage = lazy(() => import("@/pages/money/payments/record-payment"));
const ReceivedPaymentPage = lazy(() => import("@/pages/money/payments/payment-page"));
const OldChecksPreviewPage = lazy(() => import("@/pages/money/payments/old-checks"));
// Inventory scan — @zxing/browser barcode reader
const InventoryScanPage = lazy(() => import("@/pages/inventory/scan"));
// Attachments — pdfjs preview
const AttachmentsGalleryPage = lazy(() => import("@/pages/attachments/gallery"));
const MyUploadsPage = lazy(() => import("@/pages/my-uploads"));
// Admin surface — niche, rarely-visited
const AdminUsersPage = lazy(() => import("@/pages/admin/users"));
const AdminTaskQueuesPage = lazy(() => import("@/pages/admin/task-queues"));
const WorkInboxPage = lazy(() => import("@/pages/inbox/index"));
const TaskCheckPage = lazy(() => import("@/pages/inbox/check"));
const DoesntAddUpPage = lazy(() => import("@/pages/doesnt-add-up/index"));
const WhatsNewListPage = lazy(() => import("@/pages/whats-new/index"));
const WhatsNewNotePage = lazy(() => import("@/pages/whats-new/note"));
const MessagesPage = lazy(() => import("@/pages/messages/index"));
const MailroomPage = lazy(() => import("@/pages/mailroom/index"));
const MailroomAccessPage = lazy(() => import("@/pages/mailroom/access"));
const AdminUserDetailPage = lazy(() => import("@/pages/admin/user-detail"));
const PtoRequestsQueuePage = lazy(() => import("@/pages/people/pto-requests"));
const HrCockpitPage = lazy(() => import("@/pages/people/hr-cockpit"));
const AttendancePage = lazy(() => import("@/pages/people/attendance"));
const CorrectiveActionsPage = lazy(() => import("@/pages/people/corrective-actions"));
const NotificationRecipientsPage = lazy(() => import("@/pages/admin/notification-recipients"));
const HolidayPayPage = lazy(() => import("@/pages/people/holiday-pay"));
const AdminRolesPage = lazy(() => import("@/pages/admin/roles"));
const AdminWebhooksPage = lazy(() => import("@/pages/admin/webhooks"));
const OrgSettingsPage = lazy(() => import("@/pages/admin/org-settings"));
const EmailTemplatesPage = lazy(() => import("@/pages/admin/email-templates"));
const CustomerEmailsPage = lazy(() => import("@/pages/admin/customer-emails"));
const IntegrationsPage = lazy(() => import("@/pages/admin/integrations"));
const QuickBooksAdminPage = lazy(() => import("@/pages/admin/quickbooks"));
const SystemHealthPage = lazy(() => import("@/pages/admin/system-health"));
const DevFindingsPage = lazy(() => import("@/pages/support/findings"));
const NotificationsPage = lazy(() => import("@/pages/notifications/index"));
const AutoScheduleActivityPage = lazy(() => import("@/pages/admin/auto-schedule-activity"));
const SchedulingReportPage = lazy(() => import("@/pages/admin/scheduling-report"));
const PhotoImportReportsPage = lazy(() => import("@/pages/admin/photo-import-reports"));
const RecipientShadowPage = lazy(() => import("@/pages/admin/recipient-shadow"));
// #1426: the Compliance section. The registry pages moved here from /admin.
const ComplianceSectionPage = lazy(() => import("@/pages/compliance/section"));
const AuthorityDetailPage = lazy(() => import("@/pages/compliance/authority-detail"));
const AuthorityEditPage = lazy(() => import("@/pages/compliance/authority-edit"));
const AuthorityToolsPage = lazy(() => import("@/pages/compliance/authority-tools"));
const TrainingPage = lazy(() => import("@/pages/training/index"));
const WoReplayPage = lazy(() => import("@/pages/admin/wo-replay"));
const AdminJobTypesPage = lazy(() => import("@/pages/admin/job-types"));
const AdminSupplyHousesPage = lazy(() => import("@/pages/admin/supply-houses"));
const FormTemplateDetailPage = lazy(() => import("@/pages/admin/form-templates/detail"));
const FormTemplateStylePresetsPage = lazy(() => import("@/pages/admin/form-templates/style-presets"));
const FormTemplateStudioPage = lazy(() => import("@/pages/admin/form-templates/studio"));
const NewWorkOrderFormPage = lazy(() => import("@/pages/admin/form-templates/new-wo-form"));
const FormTemplateWorkflowCoveragePage = lazy(() => import("@/pages/admin/form-templates/workflow-coverage"));
const TechPointsBudgetsPage = lazy(() => import("@/pages/admin/tech-points-budgets"));
const PerformancePage = lazy(() => import("@/pages/admin/performance"));
const EmployeeReportPage = lazy(() => import("@/pages/admin/employee-report"));
// Payroll — only visited by office admins on cycle boundaries
const PayrollDashboard = lazy(() => import("@/pages/payroll/index"));
const PayrollReviewPreflight = lazy(() => import("@/pages/payroll/review-preflight"));
const PayrollJobReview = lazy(() => import("@/pages/payroll/job-review"));
const PayrollReview = lazy(() => import("@/pages/payroll/review"));
const PayrollRules = lazy(() => import("@/pages/payroll/rules"));
const PayrollProfiles = lazy(() => import("@/pages/payroll/profiles"));
const PayrollRun = lazy(() => import("@/pages/payroll/run"));
const PayrollTravel = lazy(() => import("@/pages/payroll/travel"));
const PayrollTimeClock = lazy(() => import("@/pages/payroll/timeclock"));
const PayrollOffice = lazy(() => import("@/pages/payroll/office"));
const PayrollAdjustments = lazy(() => import("@/pages/payroll/adjustments"));
const PayrollCommissions = lazy(() => import("@/pages/payroll/commissions"));
const PayrollPointBoard = lazy(() => import("@/pages/payroll/point-board"));
// Remaining secondary office pages — keep eager paths small
const SitesListPage = lazy(() => import("@/pages/sites/index"));
const IntakeDeskPage = lazy(() => import("@/pages/jobs/intake-desk"));
const WorkOrderInboxPage = lazy(() => import("@/pages/jobs/work-order-inbox"));
const WorkOrderReviewPage = lazy(() => import("@/pages/jobs/work-order-review"));
const RecurringJobsPage = lazy(() => import("@/pages/jobs/recurring"));
const JobPhotoBacklogPage = lazy(() => import("@/pages/jobs/photo-backlog"));
const PreJobReviewPage = lazy(() => import("@/pages/jobs/pre-job-review"));
const PreDispatchPaymentPage = lazy(() => import("@/pages/jobs/pre-dispatch-payment"));
const CommercialFormPage = lazy(() => import("@/pages/commercial/form"));
const EstimateTranslatePage = lazy(() => import("@/pages/commercial/translate"));
const StTranslationBackfillPage = lazy(() => import("@/pages/commercial/st-backfill"));
const InspectionPerformPage = lazy(() => import("@/pages/inspections/perform"));
const InspectionResultsPage = lazy(() => import("@/pages/inspections/results"));
const InspectionDetailPage = lazy(() => import("@/pages/inspections/detail"));
const AutomationsPage = lazy(() => import("@/pages/automations/index"));
const ReportsPage = lazy(() => import("@/pages/reports/index"));
const PartsToStagePage = lazy(() => import("@/pages/reports/parts-to-stage"));
const AiComplianceRisksPage = lazy(() => import("@/pages/compliance/ai-risks"));
const GaugesPage = lazy(() => import("@/pages/compliance/gauges"));
const ComplianceCockpitPage = lazy(() => import("@/pages/compliance/cockpit"));
const SubmissionsPage = lazy(() => import("@/pages/submissions/index"));
const PurchasingPage = lazy(() => import("@/pages/purchasing/index"));
const PoVendorsPage = lazy(() => import("@/pages/purchasing/vendors"));
const PoReviewPage = lazy(() => import("@/pages/purchasing/review"));
const DeviceIntelligencePage = lazy(() => import("@/pages/catalog/device-intelligence"));
const InventoryListPage = lazy(() => import("@/pages/inventory/index"));
const PartsCustodyPage = lazy(() => import("@/pages/inventory/parts-custody"));
const InventoryItemDetailPage = lazy(() => import("@/pages/inventory/item-detail"));
const WarehousesPage = lazy(() => import("@/pages/inventory/warehouses"));
const TransfersPage = lazy(() => import("@/pages/inventory/transfers"));
const CountsListPage = lazy(() => import("@/pages/inventory/counts").then((m) => ({ default: m.CountsListPage })));
const CountDetailPage = lazy(() => import("@/pages/inventory/counts").then((m) => ({ default: m.CountDetailPage })));
const MembershipsPage = lazy(() => import("@/pages/memberships/index"));
const ReviewsPage = lazy(() => import("@/pages/reviews/index"));
const PipelinePage = lazy(() => import("@/pages/pipeline/index"));
const PipelineDetailPage = lazy(() => import("@/pages/pipeline/detail"));
const BookingPage = lazy(() => import("@/pages/booking/index"));
const RequestsPage = lazy(() => import("@/pages/requests/index"));
const PublicBookingPage = lazy(() => import("@/pages/public/book"));
const PublicReviewPage = lazy(() => import("@/pages/public/review"));
const PublicPayPage = lazy(() => import("@/pages/public/pay"));
const PublicGroupPayPage = lazy(() => import("@/pages/public/pay-group"));
const PublicCollectCardPage = lazy(() => import("@/pages/public/collect-card"));
const PublicEstimatePage = lazy(() => import("@/pages/public/estimate"));
const PublicRfqPage = lazy(() => import("@/pages/public/rfq"));
const PublicInterviewPage = lazy(() => import("@/pages/public/interview"));
const PublicPhotosPage = lazy(() => import("@/pages/public/photos"));
const PublicReportsPage = lazy(() => import("@/pages/public/reports"));
const PublicScheduleTestPage = lazy(() => import("@/pages/public/schedule-test"));
const EmailPreviewLinkPage = lazy(() => import("@/pages/public/email-preview"));
const WarrantiesPage = lazy(() => import("@/pages/warranties/index"));
const PricebookBulkEditPage = lazy(() => import("@/pages/pricebook/bulk-edit"));
const PricebookVendorPriceFilePage = lazy(() => import("@/pages/pricebook/vendor-price-file"));
const PricebookQuoteTesterPage = lazy(() => import("@/pages/pricebook/quote-tester"));
const PricebookActionMapPage = lazy(() => import("@/pages/pricebook/action-map"));
const PricebookPartsFitPage = lazy(() => import("@/pages/pricebook/parts-fit"));
const PricebookRepairFinderPage = lazy(() => import("@/pages/pricebook/repair-finder"));
const PricebookReplacementHoursPage = lazy(() => import("@/pages/pricebook/replacement-hours"));
const PricebookHomePage = lazy(() => import("@/pages/pricebook/home"));
const PricebookBrowsePage = lazy(() => import("@/pages/pricebook/index"));
const RateCardsPage = lazy(() => import("@/pages/rate-cards/index"));
const PricebookItemDetailPage = lazy(() => import("@/pages/pricebook/detail"));
// Portal — separate auth, rarely visited from the office side
const PortalLoginPage = lazy(() => import("@/pages/portal/login"));
const PortalDashboard = lazy(() => import("@/pages/portal/dashboard"));
const PortalJobs = lazy(() => import("@/pages/portal/jobs"));
const PortalInvoices = lazy(() => import("@/pages/portal/invoices"));
const PortalEstimates = lazy(() => import("@/pages/portal/estimates"));
const PortalAssets = lazy(() => import("@/pages/portal/assets"));
const PortalBudget = lazy(() => import("@/pages/portal/budget"));

/** Service workers cache `index.html`; in dev that breaks boot after entry-point changes (#app → #root). Only enable in production builds. */
if (import.meta.env.PROD) {
    registerServiceWorker();
    window.addEventListener("load", () => {
        setTimeout(() => registerPushSubscription().catch(() => {}), 5000);
    });
}

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: 30_000,
            // One retry, except for a 429: the limiter window hasn't rolled, so
            // a retry just spends more of the same allowance and keeps the
            // screen failing longer.
            retry: (failureCount, error) => !(error instanceof ApiError && error.isRateLimited) && failureCount < 1,
            refetchOnWindowFocus: false,
            // #1526 — ATTEMPT the request even when we believe we're offline.
            // The default ("online") pauses the query instead, which means the
            // service worker — holding the last-known response for this exact
            // URL in `gwc-api`, ready to serve — is never asked for it. A tech
            // in a dead zone got a spinner over data his phone already had.
            // Retries still pause while offline, so a real dead zone is not
            // hammered.
            networkMode: "offlineFirst",
        },
        mutations: {
            // The one that was losing work. Every field save queues itself from
            // INSIDE its mutationFn (saveThroughOutbox / queueOfflineAction), so
            // a mutation paused by the default "online" mode never reached the
            // code that puts the save in the outbox — it sat in memory, and was
            // gone if the tech closed the app before signal returned. Attempting
            // once means the save always reaches its own queueing path.
            networkMode: "offlineFirst",
        },
    },
    // Global safety net: any failed mutation that doesn't handle its own error
    // surfaces a visible toast instead of failing silently. Before this, a save
    // whose useMutation lacked an onError (e.g. a field test/inspection write
    // that 500s/422s) showed the user nothing — the screen just looked like it
    // saved. App-wide, so every current and future mutation is covered.
    mutationCache: new MutationCache({
        onError: (error, _variables, _context, mutation) => {
            // A mutation with its own onError already surfaces a message — don't
            // double-toast.
            if (mutation.options.onError) return;
            // Opt-out for intentionally-silent background saves
            // (set meta: { silentError: true } on the useMutation).
            if (mutation.meta?.silentError) return;
            const serverMessage =
                error instanceof ApiError &&
                typeof error.body === "object" &&
                error.body !== null &&
                "message" in error.body
                    ? String((error.body as { message?: unknown }).message ?? "")
                    : "";
            toast.error(serverMessage || "Something went wrong. Please try again.");
        },
    }),
});

function RequireAuth({ children }: { children: React.ReactNode }) {
    const { user, loading, unreachable, retryAuth } = useAuth();
    if (loading) return <PageSpinner />;
    // We hold a session we could not verify. Redirecting to /login would claim
    // the session is gone when we simply couldn't ask, and the old behaviour —
    // spinning here forever — is the bug itself (#1360). Say so, offer retry.
    if (unreachable && !user) return <AuthUnreachable onRetry={retryAuth} />;
    if (!user) {
        rememberReturnPath(`${window.location.pathname}${window.location.search}`);
        return <Navigate to="/login" replace />;
    }
    return <>{children}</>;
}

/**
 * The office/admin home screen ('/') is the Command Center (Office-Day view).
 * Field-only technicians are redirected to their job board so home isn't full
 * of office noise; PhotoImporter temp accounts go to their single allowed page.
 *
 * The Command Center became home in the owner-gated cutover (Issue #138),
 * replacing the classic dashboard that used to render here (behind the old
 * command_queue.pilot gate). The classic dashboard is still reachable at
 * /dashboard as a fallback, and the Command Queue pilot page stays at /command.
 */
function HomeRoute() {
    const { isField, isOffice, isAdmin, isPhotoImporter } = useAuth();
    if (isPhotoImporter) {
        return <Navigate to="/photo-import" replace />;
    }
    if (isField && !isOffice && !isAdmin) {
        return <Navigate to="/field" replace />;
    }
    return <CommandCenterNextPage />;
}

/**
 * Wraps a route so that PhotoImporter logins are redirected to the
 * single page they're allowed to see. The PhotoImporter role exists
 * solely for the ServiceTitan photo-harvesting workflow; every other
 * surface in the app must be off-limits to those temp-worker accounts.
 */
function BlockPhotoImporters({ children }: { children: React.ReactNode }) {
    const { isPhotoImporter } = useAuth();
    if (isPhotoImporter) return <Navigate to="/photo-import" replace />;
    return <>{children}</>;
}

/**
 * `/admin/water-providers/:id` → `/compliance/authorities/:id` (#1426). A
 * plain <Navigate> cannot carry the param, so this reads it and forwards.
 */
function RedirectWithId({ to }: { to: string }) {
    const { id } = useParams<{ id: string }>();
    return <Navigate to={id ? `${to}/${id}` : to} replace />;
}

/**
 * `/ci/:id` → the HR Cockpit with that call-in pinned on top. The short form
 * exists for the link in a call-in text message, which is read on a phone.
 */
function CallInLink() {
    const { id } = useParams<{ id: string }>();
    return <Navigate to={id ? `/people/hr-cockpit?callIn=${encodeURIComponent(id)}` : "/people/hr-cockpit"} replace />;
}

function PortalRoutes() {
    return (
        <PortalAuthProvider>
            <Routes>
                <Route path="login" element={<PortalLoginPage />} />
                <Route element={<PortalShell />}>
                    <Route index element={<PortalDashboard />} />
                    <Route path="jobs" element={<PortalJobs />} />
                    <Route path="invoices" element={<PortalInvoices />} />
                    <Route path="estimates" element={<PortalEstimates />} />
                    <Route path="assets" element={<PortalAssets />} />
                    <Route path="budget" element={<PortalBudget />} />
                </Route>
            </Routes>
        </PortalAuthProvider>
    );
}

function AppRoutes() {
    return (
        <Routes>
            <Route path="/portal/*" element={<PortalRoutes />} />
            <Route path="/r/:token" element={<PublicReviewPage />} />
            <Route path="/book/:slug" element={<PublicBookingPage />} />
            <Route path="/pay/:token" element={<PublicPayPage />} />
            <Route path="/pay/group/:token" element={<PublicGroupPayPage />} />
            <Route path="/card/:token" element={<PublicCollectCardPage />} />
            <Route path="/estimate/:token" element={<PublicEstimatePage />} />
            <Route path="/rfq/:token" element={<PublicRfqPage />} />
            <Route path="/interview/:token" element={<PublicInterviewPage />} />
            <Route path="/photos/:token" element={<PublicPhotosPage />} />
            <Route path="/test-reports/:token" element={<PublicReportsPage />} />
            <Route path="/schedule/:token" element={<PublicScheduleTestPage />} />
            <Route path="/email-preview/:what" element={<EmailPreviewLinkPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/auth/google/return" element={<GoogleReturnPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/set-password" element={<SetPasswordPage />} />
            {/* Photo Import is mounted OUTSIDE AppShell — temp PhotoImporter
                accounts get a full-screen, single-purpose surface with no
                nav / breadcrumbs / quick-add to wander into. */}
            <Route
                path="/photo-import"
                element={
                    <RequireAuth>
                        <PhotoImportPage />
                    </RequireAuth>
                }
            />
            <Route
                element={
                    <RequireAuth>
                        <BlockPhotoImporters>
                            <ClockInGate>
                                <AppShell />
                            </ClockInGate>
                        </BlockPhotoImporters>
                    </RequireAuth>
                }
            >
                <Route index element={<HomeRoute />} />
                {/* Home ('/') renders the Command Center (Office-Day) for
                    office/admin. The classic dashboard it replaced stays here
                    as a reversible fallback, and the Command Queue pilot page
                    keeps its own URL (Issue #138). */}
                <Route path="dashboard" element={<DashboardPage />} />
                <Route path="command" element={<CommandPage />} />
                <Route path="inbox" element={<WorkInboxPage />} />
                {/* SMS-1: the office's two-way SMS inbox — the command
                    palette and dispatch shell linked here before the page
                    existed. */}
                <Route path="messages" element={<MessagesPage />} />
                {/* v3 toolbar destinations (TOOLBAR-SPEC.md). Tasks aliases the
                    existing work inbox (its "On you now" == the Needs-you list);
                    Mailroom is a routed stub until its full page ships. */}
                <Route path="tasks" element={<WorkInboxPage />} />
                <Route path="tasks/check" element={<TaskCheckPage />} />
                <Route path="doesnt-add-up" element={<DoesntAddUpPage />} />
                <Route path="whats-new" element={<WhatsNewListPage />} />
                <Route path="whats-new/:id" element={<WhatsNewNotePage />} />
                <Route path="mailroom" element={<MailroomPage />} />
                <Route path="mailroom/access" element={<MailroomAccessPage />} />
                <Route path="jobs" element={<JobsPage />} />
                <Route path="jobs/schedule" element={<SchedulePage />} />
                <Route path="jobs/work-orders" element={<IntakeDeskPage />} />
                {/* #825 m3: breadcrumbs and habit both type /intake — it used
                    to 404. Read-only alias for the Intake Desk. */}
                <Route path="intake" element={<Navigate to="/jobs/work-orders" replace />} />
                <Route path="jobs/work-orders/all" element={<WorkOrderInboxPage />} />
                <Route path="jobs/work-orders/setup/:itemId" element={<WorkOrderReviewPage />} />
                <Route path="jobs/work-orders/review/:itemId" element={<WorkOrderReviewPage />} />
                <Route path="jobs/photo-backlog" element={<JobPhotoBacklogPage />} />
                <Route path="jobs/pre-job-review" element={<PreJobReviewPage />} />
                <Route path="jobs/pre-dispatch-payment" element={<PreDispatchPaymentPage />} />
                <Route path="jobs/new" element={<JobFormPage />} />
                <Route path="jobs/recurring" element={<RecurringJobsPage />} />
                <Route path="jobs/:id/edit" element={<JobFormPage />} />
                <Route path="jobs/:id" element={<JobDetailPage />} />
                <Route path="customers" element={<CustomersPage />} />
                <Route path="customers/new" element={<CustomerIntakePage />} />
                <Route path="customers/:id/edit" element={<CustomerFormPage />} />
                <Route path="customers/:id" element={<CustomerDetailPage />} />
                <Route path="sites" element={<SitesListPage />} />
                <Route path="sites/:siteId" element={<SiteDetailPage />} />
                <Route path="assets/:assetId" element={<AssetDetailPage />} />
                <Route path="commercial" element={<CommercialPage />} />
                <Route path="commercial/new" element={<CommercialFormPage />} />
                <Route path="commercial/:id" element={<CommercialDetailPage />} />
                <Route path="commercial/:id/translate" element={<EstimateTranslatePage />} />
                <Route path="commercial/st-backfill" element={<StTranslationBackfillPage />} />
                {/* Dedicated invoice route — opens the invoice detail directly
                    (line items, send, record payment). Reuses the commercial
                    detail page locked to invoice mode. */}
                <Route path="invoices/:invoiceId" element={<CommercialDetailPage forceType="invoice" />} />
                <Route path="dispatch" element={<DispatchPage />} />
                <Route path="dispatch/map" element={<DispatchMapPage />} />
                <Route path="dispatch/route-planner" element={<DispatchRoutePlannerPage />} />
                <Route path="ops" element={<OpsCenterPage />} />
                {/* The old Catalog page (Materials / Services / Vendors, its own cents
                    prices) is retired — the Fieldbook is the only catalog (#1458 stage 4). */}
                <Route path="catalog" element={<Navigate to="/pricebook" replace />} />
                <Route path="field" element={<FieldPage />} />
                <Route path="field/jobs/:jobId" element={<FieldJobDetailPage />} />
                <Route path="field/points" element={<FieldPoints />} />
                <Route path="field/profile" element={<FieldProfilePage />} />
                <Route path="field/time-off" element={<FieldTimeOffPage />} />
                <Route path="field/backflow/:jobId" element={<BackflowWorkflowPage />} />
                <Route path="field/fire-safety/:jobId" element={<FireSafetyWorkflowPage />} />
                <Route path="field/work-order/:jobId" element={<WorkOrderWorkflowPage />} />
                <Route path="field/work-order-pdf/:jobId" element={<WorkOrderPdfEditorPage />} />
                <Route path="field/chat" element={<FieldChatPage />} />
                {/* Bare workflow list paths have no page of their own — the field
                    home screen IS the list. Redirect so breadcrumb links and typed
                    URLs never strand anyone on a blank screen. */}
                <Route path="field/jobs" element={<Navigate to="/field" replace />} />
                <Route path="field/backflow" element={<Navigate to="/field" replace />} />
                <Route path="field/fire-safety" element={<Navigate to="/field" replace />} />
                <Route path="field/work-order" element={<Navigate to="/field" replace />} />
                <Route path="field/supplies" element={<Navigate to="/field" replace />} />
                <Route path="help" element={<HelpPage />} />
                <Route path="help/filing-walkthrough" element={<FilingWalkthroughPage />} />
                <Route path="payroll" element={<PayrollDashboard />} />
                <Route path="payroll/review-preflight" element={<PayrollReviewPreflight />} />
                <Route path="payroll/job-review" element={<PayrollJobReview />} />
                <Route path="payroll/review" element={<PayrollReview />} />
                <Route path="payroll/rules" element={<PayrollRules />} />
                <Route path="payroll/profiles" element={<PayrollProfiles />} />
                <Route path="payroll/run" element={<PayrollRun />} />
                <Route path="payroll/travel" element={<PayrollTravel />} />
                <Route path="payroll/timeclock" element={<PayrollTimeClock />} />
                <Route path="payroll/office" element={<PayrollOffice />} />
                <Route path="payroll/adjustments" element={<PayrollAdjustments />} />
                <Route path="payroll/commissions" element={<PayrollCommissions />} />
                <Route path="payroll/point-board" element={<PayrollPointBoard />} />
                <Route path="inspections" element={<InspectionResultsPage />} />
                <Route path="inspections/perform/:jobId/:templateId" element={<InspectionPerformPage />} />
                <Route path="inspections/perform/:jobId" element={<InspectionPerformPage />} />
                <Route path="inspections/:id" element={<InspectionDetailPage />} />
                {/* #1426: the Compliance section — the work (licenses, city
                    filing) and set-up (water providers and the other config
                    screens that used to be the Settings "Compliance Rules"
                    room). Old /admin paths redirect below; nothing is deleted. */}
                <Route path="compliance" element={<ComplianceSectionPage />} />
                <Route path="compliance/authorities/tools" element={<AuthorityToolsPage />} />
                <Route path="compliance/authorities/:id/edit" element={<AuthorityEditPage />} />
                <Route path="compliance/authorities/:id" element={<AuthorityDetailPage />} />
                <Route path="compliance/cockpit" element={<ComplianceCockpitPage />} />
                <Route path="scheduling/compliance-due" element={<CompliancePage />} />
                <Route path="attachments" element={<AttachmentsGalleryPage />} />
                <Route path="my-uploads" element={<MyUploadsPage />} />
                <Route path="automations" element={<AutomationsPage />} />
                <Route path="reports" element={<ReportsPage />} />
                <Route path="reports/parts-to-stage" element={<PartsToStagePage />} />
                <Route path="analytics" element={<AnalyticsPage />} />
                <Route path="analytics/profitability" element={<ProfitabilityPage />} />
                <Route path="profitability" element={<ProfitabilityPage />} />
                <Route path="profitability/:id" element={<ProfitabilityDetailPage />} />
                <Route path="analytics/profitability/:id" element={<ProfitabilityDetailPage />} />
                <Route path="money" element={<MoneyPage />} />
                <Route path="money/payments/new" element={<RecordPaymentPage />} />
                <Route path="money/payments/old-checks" element={<OldChecksPreviewPage />} />
                <Route path="money/payments/:id" element={<ReceivedPaymentPage />} />
                {/* Money hub collapse (wave 1): the old finance pages live on
                    as tabs of /money — every old URL redirects to its tab so
                    deep links and bookmarks keep working. */}
                <Route path="accounting/collections" element={<Navigate to="/money" replace />} />
                <Route path="accounting/ar-aging" element={<Navigate to="/money?tab=invoices" replace />} />
                <Route path="accounting/invoices-attention" element={<Navigate to="/money?tab=invoices" replace />} />
                <Route path="accounting/estimates-attention" element={<Navigate to="/money?tab=estimates" replace />} />
                <Route path="accounting/failed-payments" element={<Navigate to="/money?tab=payments" replace />} />
                <Route path="credit-memos" element={<Navigate to="/money?tab=payments" replace />} />
                <Route path="accounting/credit-memos" element={<Navigate to="/money?tab=payments" replace />} />
                <Route path="memberships" element={<MembershipsPage />} />
                <Route path="reviews" element={<ReviewsPage />} />
                <Route path="pipeline" element={<PipelinePage />} />
                <Route path="pipeline/:id" element={<PipelineDetailPage />} />
                <Route path="booking" element={<BookingPage />} />
                <Route path="requests" element={<RequestsPage />} />
                <Route path="admin/booking" element={<BookingPage />} />
                <Route path="warranties" element={<WarrantiesPage />} />
                <Route path="inventory/warranties" element={<WarrantiesPage />} />
                <Route path="pricebook" element={<PricebookHomePage />} />
                <Route path="pricebook/browse" element={<PricebookBrowsePage />} />
                <Route path="rate-cards" element={<RateCardsPage />} />
                <Route path="pricebook/items/:id" element={<PricebookItemDetailPage />} />
                <Route path="pricebook/bulk-edit" element={<PricebookBulkEditPage />} />
                <Route path="pricebook/vendor-price-file" element={<PricebookVendorPriceFilePage />} />
                <Route path="pricebook/action-map" element={<PricebookActionMapPage />} />
                <Route path="pricebook/parts-fit" element={<PricebookPartsFitPage />} />
                <Route path="pricebook/repair-finder" element={<PricebookRepairFinderPage />} />
                <Route path="pricebook/replacement-hours" element={<PricebookReplacementHoursPage />} />
                <Route path="pricebook/quote-tester" element={<PricebookQuoteTesterPage />} />
                {/* /admin/users duplicated /people/{office,field} (same component,
                    switcher tab) — one canonical route each. The :id detail below stays. */}
                <Route path="admin/users" element={<Navigate to="/people/office" replace />} />
                <Route path="people/office" element={<AdminUsersPage section="office" />} />
                <Route path="people/field" element={<AdminUsersPage section="field" />} />
                <Route path="people/pto-requests" element={<PtoRequestsQueuePage />} />
                <Route path="people/hr-cockpit" element={<HrCockpitPage />} />
                <Route path="ci/:id" element={<CallInLink />} />
                <Route path="people/attendance" element={<AttendancePage />} />
                <Route path="people/corrective-actions" element={<CorrectiveActionsPage />} />
                <Route path="people/notification-recipients" element={<NotificationRecipientsPage />} />
                <Route path="people/holiday-pay" element={<HolidayPayPage />} />
                <Route path="admin/users/:id" element={<AdminUserDetailPage />} />
                <Route path="admin/roles" element={<AdminRolesPage />} />
                <Route path="admin/task-queues" element={<AdminTaskQueuesPage />} />
                <Route path="admin/webhooks" element={<AdminWebhooksPage />} />
                <Route path="admin/org-settings" element={<OrgSettingsPage />} />
                <Route path="admin/email-templates" element={<EmailTemplatesPage />} />
                <Route path="admin/customer-emails" element={<CustomerEmailsPage />} />
                <Route path="admin/integrations" element={<IntegrationsPage />} />
                <Route path="admin/quickbooks" element={<QuickBooksAdminPage />} />
                {/* Money hub collapse (wave 1): sync issues are the hub's QuickBooks tab. */}
                <Route path="admin/qbo-sync-issues" element={<Navigate to="/money?tab=quickbooks" replace />} />
                <Route path="admin/system-health" element={<SystemHealthPage />} />
                <Route path="admin/employee-report" element={<EmployeeReportPage />} />
                <Route path="support/findings" element={<DevFindingsPage />} />
                {/* Any signed-in user: techs have the bell too (#996). */}
                <Route path="notifications" element={<NotificationsPage />} />
                <Route path="admin/auto-schedule-activity" element={<AutoScheduleActivityPage />} />
                <Route path="admin/scheduling-report" element={<SchedulingReportPage />} />
                <Route path="admin/photo-import-reports" element={<PhotoImportReportsPage />} />
                <Route path="admin/recipient-shadow" element={<RecipientShadowPage />} />
                <Route path="compliance/ai-risks" element={<AiComplianceRisksPage />} />
                <Route path="compliance/gauges/full" element={<GaugesPage />} />
                <Route path="compliance/:tab" element={<ComplianceSectionPage />} />
                <Route path="scheduling/test-due" element={<TestDueRosterPage />} />
                <Route path="office-review" element={<OfficeReviewPage />} />
                <Route path="close-out" element={<CloseOutPage />} />
                <Route path="close-out/report-input" element={<ReportInputQueuePage />} />
                <Route path="command-center-next" element={<CommandCenterNextPage />} />
                {/* #1426 redirects — every old registry path keeps resolving. */}
                <Route path="admin/water-providers" element={<Navigate to="/compliance/authorities" replace />} />
                <Route path="admin/water-providers/:id" element={<RedirectWithId to="/compliance/authorities" />} />
                <Route path="admin/bulk-jurisdiction" element={<Navigate to="/compliance/jurisdictions" replace />} />
                <Route path="admin/supply-houses" element={<AdminSupplyHousesPage />} />
                <Route path="admin/reporting-platforms" element={<Navigate to="/compliance/platforms" replace />} />
                <Route path="training" element={<TrainingPage />} />
                <Route path="admin/wo-vendors" element={<Navigate to="/compliance/wo-vendors" replace />} />
                <Route path="admin/wo-replay" element={<WoReplayPage />} />
                <Route path="admin/coi" element={<Navigate to="/compliance/coi" replace />} />
                <Route path="admin/job-types" element={<AdminJobTypesPage />} />
                <Route path="admin/license-types" element={<Navigate to="/compliance/license-types" replace />} />
                <Route path="admin/form-templates" element={<Navigate to="/compliance/forms" replace />} />
                <Route path="admin/form-templates/style-presets" element={<FormTemplateStylePresetsPage />} />
                <Route path="admin/form-templates/coverage" element={<FormTemplateWorkflowCoveragePage />} />
                <Route path="admin/form-templates/:id/studio" element={<FormTemplateStudioPage />} />
                <Route path="admin/form-templates/:id/new" element={<NewWorkOrderFormPage />} />
                <Route path="admin/form-templates/:id" element={<FormTemplateDetailPage />} />
                <Route path="admin/tech-points-budgets" element={<TechPointsBudgetsPage />} />
                <Route path="admin/performance" element={<PerformancePage />} />
                <Route path="submissions" element={<SubmissionsPage />} />
                <Route path="purchasing" element={<PurchasingPage />} />
                <Route path="purchasing/vendors" element={<PoVendorsPage />} />
                <Route path="purchasing/:poId/review" element={<PoReviewPage />} />
                <Route path="catalog/device-intelligence" element={<DeviceIntelligencePage />} />
                <Route path="inventory" element={<InventoryListPage />} />
                <Route path="inventory/parts-custody" element={<PartsCustodyPage />} />
                <Route path="inventory/scan" element={<InventoryScanPage />} />
                <Route path="inventory/warehouses" element={<WarehousesPage />} />
                <Route path="inventory/transfers" element={<TransfersPage />} />
                <Route path="inventory/counts" element={<CountsListPage />} />
                <Route path="inventory/counts/:id" element={<CountDetailPage />} />
                <Route path="inventory/:id" element={<InventoryItemDetailPage />} />
                {/* The Settings console — 7-room home for all set-once config
                    (mockups/gwc-more-section.html). Deep tabs keep their own
                    routes too; ?room=&tab= restores a specific room/tab. */}
                <Route path="settings" element={<SettingsConsolePage />} />
                <Route path="profile" element={<ProfilePage />} />
                {/* Legacy paths from the pre-launch IA — redirect to their
                    modern equivalents so old bookmarks keep working. */}
                <Route path="schedule" element={<Navigate to="/jobs/schedule" replace />} />
                <Route path="work-orders" element={<Navigate to="/jobs/work-orders" replace />} />
                <Route path="users" element={<Navigate to="/people/office" replace />} />
                <Route path="team" element={<Navigate to="/field/chat" replace />} />
                {/* Safety net: any unmatched path inside the shell renders a real
                    not-found page instead of blank chrome. Must stay last. */}
                <Route path="*" element={<NotFoundPage />} />
            </Route>
        </Routes>
    );
}

const rootEl = document.getElementById("root");
if (!rootEl) {
    document.body.innerHTML = `
<div style="font-family:system-ui,sans-serif;padding:2rem;max-width:42rem;line-height:1.5;color:#0f172a;background:#fff">
  <h1 style="font-size:1.25rem;margin:0 0 0.75rem">Missing #root — stale cache?</h1>
  <p style="margin:0 0 1rem">This build expects <code>&lt;div id="root"&gt;</code> in <code>index.html</code>.
  A <strong>service worker</strong> may still be serving an older HTML file (from the legacy hash shell).</p>
  <p style="margin:0 0 1rem"><strong>Fix:</strong> Chrome → DevTools (<kbd>F12</kbd>) → <strong>Application</strong> → <strong>Service Workers</strong> → <em>Unregister</em>,
  then <strong>Storage</strong> → <em>Clear site data</em> for <code>http://localhost:5173</code>.
  Close the tab, reopen <code>http://localhost:5173/</code>, hard refresh (<kbd>⌘⇧R</kbd> / <kbd>Ctrl+Shift+R</kbd>).</p>
</div>`;
    throw new Error("Missing DOM element #root — clear localhost storage and unregister service workers.");
}

createRoot(rootEl).render(
    <StrictMode>
        <ErrorBoundary>
            <QueryClientProvider client={queryClient}>
                <BrowserRouter>
                    <AuthProvider>
                        <OfflineIndicator />
                        <NormalizePath />
                        <AppRoutes />
                        {/* The v3 toolbar's command search is the single ⌘K
                            palette now (TOOLBAR-SPEC.md); the standalone modal
                            palette is retired to avoid two ⌘K handlers. */}
                        <Toaster />
                        <AppDialogHost />
                        <InstallPrompt />
                        <UpdateBanner />
                        {/* OFFLINE-QUEUE HONESTY — persistent red "N saves didn't
                            sync" chip. App-wide mount (like UpdateBanner): the
                            outbox is device-local, so it only ever shows on the
                            tech device that owns the failed saves. */}
                        <FailedSavesChip />
                    </AuthProvider>
                </BrowserRouter>
            </QueryClientProvider>
        </ErrorBoundary>
    </StrictMode>,
);
